import { useEffect, useMemo, useRef, useState } from "react";
import { numberLocaleFor } from "../i18n/locale";
import type { Locale } from "../i18n/locale";
import { LINE_ITEM_COLORS } from "../lib/chartColors";
import type { DisplayCurrency } from "../lib/displayCurrency";
import { usdToEurIndicative } from "../lib/displayCurrency";
import type { CostEstimate } from "../lib/types";
import type { UiKey } from "../i18n/ui";

const HISTORY_CAP = 32;
const SPARK_W = 320;
const SPARK_H = 56;
const SPARK_PAD = 4;

type Props = {
  locale: Locale;
  t: (key: UiKey) => string;
  estimate: CostEstimate;
  displayCurrency?: DisplayCurrency;
};

export function LiveEstimatePulse({
  locale,
  t,
  estimate,
  displayCurrency = "USD",
}: Props) {
  const nf = numberLocaleFor(locale);
  const total = estimate.totalMonthlyUsd;
  const cur = displayCurrency === "EUR" ? "EUR" : "USD";
  const toDisp = (usd: number) =>
    displayCurrency === "EUR" ? usdToEurIndicative(usd) : usd;
  const historyRef = useRef<number[]>([]);
  const [histTick, setHistTick] = useState(0);

  useEffect(() => {
    historyRef.current = [...historyRef.current.slice(-(HISTORY_CAP - 1)), total];
    setHistTick((v) => v + 1);
  }, [total]);

  const history = historyRef.current;
  const historyDisp =
    displayCurrency === "EUR" ? history.map((v) => usdToEurIndicative(v)) : history;

  const fmt = (n: number) =>
    n.toLocaleString(nf, {
      style: "currency",
      currency: cur,
      maximumFractionDigits: n >= 100 ? 0 : 2,
    });

  const topLines = useMemo(() => {
    return [...estimate.lineItems]
      .filter((r) => r.monthlyUsd > 0)
      .sort((a, b) => b.monthlyUsd - a.monthlyUsd)
      .slice(0, 5);
  }, [estimate.lineItems]);

  const mixPct = (n: number) => (total > 0 ? Math.max(0.25, (n / total) * 100) : 0);

  let sparkPoints = "";
  if (historyDisp.length >= 2) {
    const maxH = Math.max(...historyDisp, 1e-9);
    const minH = Math.min(...historyDisp);
    const span = Math.max(maxH - minH, maxH * 0.02, 1);
    sparkPoints = historyDisp
      .map((v, i) => {
        const x =
          SPARK_PAD + (i / (historyDisp.length - 1)) * (SPARK_W - SPARK_PAD * 2);
        const norm = (v - minH) / span;
        const y = SPARK_PAD + (1 - norm) * (SPARK_H - SPARK_PAD * 2);
        return `${x.toFixed(1)},${y.toFixed(1)}`;
      })
      .join(" ");
  }

  const showSpark = sparkPoints.length > 0;
  const isEmpty = total <= 0 && topLines.length === 0;

  return (
    <div className="lep" data-live-tick={histTick}>
      <div className="lep-head">
        <h2>{t("livePulse.title")}</h2>
        <p className="lep-sub">{t("livePulse.sub")}</p>
      </div>

      {isEmpty ? (
        <p className="lep-empty">{t("livePulse.empty")}</p>
      ) : (
        <>
          <p className="lep-total" aria-live="polite">
            <span className="lep-total-val">{fmt(toDisp(total))}</span>
            <span className="lep-total-unit">{t("breakdown.perMonth")}</span>
          </p>

          <div
            className="lep-stack"
            role="img"
            aria-label={t("mix.title")}
            title={estimate.lineItems
              .map((r) => `${r.label}: ${fmt(toDisp(r.monthlyUsd))}`)
              .join(" · ")}
          >
            {estimate.lineItems.map((row) => (
              <div
                key={row.id}
                className="lep-stack-seg"
                style={{
                  width: `${mixPct(row.monthlyUsd)}%`,
                  background: LINE_ITEM_COLORS[row.id] ?? "#666",
                }}
              />
            ))}
          </div>

          {topLines.length > 0 ? (
            <div className="lep-top">
              <p className="lep-cap">{t("livePulse.topCaption")}</p>
              <ul className="lep-bars">
                {topLines.map((row) => {
                  const p = total > 0 ? (row.monthlyUsd / total) * 100 : 0;
                  return (
                    <li key={row.id}>
                      <span className="lep-bars-lab">{row.label}</span>
                      <div className="lep-bars-track">
                        <div
                          className="lep-bars-fill"
                          style={{
                            width: `${p}%`,
                            background: LINE_ITEM_COLORS[row.id] ?? "#666",
                          }}
                        />
                      </div>
                      <span className="lep-bars-pct">
                        {total > 0
                          ? `${p.toLocaleString(nf, { maximumFractionDigits: 0 })}%`
                          : "—"}
                      </span>
                    </li>
                  );
                })}
              </ul>
            </div>
          ) : null}

          {showSpark ? (
            <div className="lep-spark-block">
              <p className="lep-cap">{t("livePulse.sparkCaption")}</p>
              <svg
                className="lep-spark"
                viewBox={`0 0 ${SPARK_W} ${SPARK_H}`}
                preserveAspectRatio="none"
                role="img"
                aria-label={t("livePulse.sparkAria")}
              >
                <defs>
                  <linearGradient id="lep-spark-grad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="var(--accent)" stopOpacity="0.35" />
                    <stop offset="100%" stopColor="var(--accent)" stopOpacity="0.02" />
                  </linearGradient>
                </defs>
                <polygon
                  className="lep-spark-fill"
                  fill="url(#lep-spark-grad)"
                  points={`${sparkPoints} ${SPARK_W - SPARK_PAD},${SPARK_H} ${SPARK_PAD},${SPARK_H}`}
                />
                <polyline className="lep-spark-line" fill="none" points={sparkPoints} />
              </svg>
            </div>
          ) : null}
        </>
      )}

      <style>{`
        .lep {
          display: flex;
          flex-direction: column;
          gap: 1rem;
        }
        .lep-head h2 {
          font-size: 1rem;
          margin: 0 0 0.35rem;
        }
        .lep-sub {
          margin: 0;
          font-size: 0.8rem;
          color: var(--muted);
          line-height: 1.45;
          max-width: 40rem;
        }
        .lep-empty {
          margin: 0;
          font-size: 0.88rem;
          color: var(--muted);
        }
        .lep-total {
          margin: 0;
          display: flex;
          flex-wrap: wrap;
          align-items: baseline;
          gap: 0.35rem 0.5rem;
        }
        .lep-total-val {
          font-size: clamp(1.65rem, 4vw, 2.15rem);
          font-weight: 700;
          font-variant-numeric: tabular-nums;
          letter-spacing: -0.03em;
          color: var(--accent);
          transition: color 0.2s ease;
        }
        .lep-total-unit {
          font-size: 0.9rem;
          color: var(--muted);
          font-weight: 600;
        }
        .lep-stack {
          display: flex;
          height: 14px;
          border-radius: 7px;
          overflow: hidden;
          background: var(--surface2);
          border: 1px solid var(--border);
        }
        .lep-stack-seg {
          min-width: 0;
          height: 100%;
          transition: width 0.45s cubic-bezier(0.33, 1, 0.68, 1);
        }
        .lep-cap {
          margin: 0 0 0.4rem;
          font-size: 0.72rem;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.06em;
          color: var(--muted);
        }
        .lep-bars {
          list-style: none;
          margin: 0;
          padding: 0;
          display: flex;
          flex-direction: column;
          gap: 0.45rem;
        }
        .lep-bars li {
          display: grid;
          grid-template-columns: minmax(0, 7.5rem) 1fr auto;
          gap: 0.45rem 0.55rem;
          align-items: center;
          font-size: 0.78rem;
        }
        .lep-bars-lab {
          color: var(--muted);
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }
        .lep-bars-track {
          height: 8px;
          border-radius: 4px;
          background: var(--surface2);
          border: 1px solid var(--border);
          overflow: hidden;
        }
        .lep-bars-fill {
          height: 100%;
          border-radius: 3px;
          transition: width 0.45s cubic-bezier(0.33, 1, 0.68, 1);
        }
        .lep-bars-pct {
          font-family: var(--mono);
          font-size: 0.72rem;
          color: var(--text);
          min-width: 2.25rem;
          text-align: right;
        }
        .lep-spark-block {
          margin-top: 0.25rem;
        }
        .lep-spark {
          width: 100%;
          height: 3.25rem;
          display: block;
          color: var(--accent);
        }
        .lep-spark-line {
          stroke: currentColor;
          stroke-width: 2;
          stroke-linejoin: round;
          stroke-linecap: round;
          vector-effect: non-scaling-stroke;
        }
        .lep-spark-fill {
          stroke: none;
        }
      `}</style>
    </div>
  );
}
